import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../applicationUi/components/Table";
import { logInfo } from "../../utils/logger";
import { fetchInvoiceById } from "../../store/thunks/invoiceThunk";
import { clearCurrentInvoice } from "../../store/slices/invoiceSlice";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import { fetchPaymentsByInvoice } from "../../store/store";
import ViewInvoicePDFModal from "../../modals/ViewInvoicePDFModal";

const ClientInvoices = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const itemsPerPage = 10;
  const dispatch = useDispatch();
  const currentInvoice = useSelector((state) => state.invoices.currentInvoice);
  const client = useSelector((state) => state.clients.selectedClient);

  if (!client) {
    return (
      <div className="p-4 text-gray-600">Loading client information...</div>
    );
  }

  const invoices = client.invoices || [];
  const totalInvoices = invoices.length;
  const totalPages = Math.ceil(totalInvoices / itemsPerPage);

  // Sorting logic
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valueA =
      sortConfig.key === "amount"
        ? parseFloat(a[sortConfig.key])
        : a[sortConfig.key];
    const valueB =
      sortConfig.key === "amount"
        ? parseFloat(b[sortConfig.key])
        : b[sortConfig.key];

    if (sortConfig.key === "createdAt" || sortConfig.key === "serviceDate") {
      return sortConfig.direction === "ascending"
        ? new Date(valueA) - new Date(valueB)
        : new Date(valueB) - new Date(valueA);
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      return sortConfig.direction === "ascending"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }

    return sortConfig.direction === "ascending"
      ? valueA - valueB
      : valueB - valueA;
  });

  const handleSortChange = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    logInfo("Invoice page changed", { newPage });
  };

  const handleInvoiceGeneratorClose = () => {
    setShowInvoiceGenerator(false);
    dispatch(clearCurrentInvoice());
  };

  const handleRowClick = async (clickedInvoice) => {
    console.log("Clicked invoice", clickedInvoice);
    const invoice = await dispatch(fetchInvoiceById(clickedInvoice)).unwrap();
    console.log("Current invoice", invoice);

    await dispatch(fetchAppointmentById(invoice.appointmentId));
    await dispatch(fetchPaymentsByInvoice(invoice._id));
    setShowInvoiceGenerator(true);
  };

  const paginatedInvoices = sortedInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-4">
      <Table
        columns={[
          { header: "Invoice ID", accessor: "invoiceId", sortable: true },
          {
            header: "Invoice Date",
            accessor: (row) => {
              const date = new Date(row.createdAt);
              return date.toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            },
            sortable: true,
          },
          {
            header: "Service",
            accessor: (row) => row.services?.[0]?.description || "Unknown",
            sortable: true,
          },
          {
            header: "Service Date",
            accessor: (row) => {
              const date = new Date(row.serviceDate);
              return date.toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            },
            sortable: true,
          },
          {
            header: "Clinician",
            accessor: (row) =>
              row.clinician
                ? `${row.clinician.firstName} ${row.clinician.lastName}`
                : "Unknown",
            sortable: true,
          },
          { header: "Amount", accessor: "amount", sortable: true },
          {
            header: "Split Invoice?",
            accessor: (row) => (row.splitBilling?.isSplit ? "Yes" : "No"),
            sortable: true,
          },
          { header: "Status", accessor: "status", sortable: true },
        ]}
        data={paginatedInvoices}
        onRowClick={(clickedInvoice) => handleRowClick(clickedInvoice)}
        title="Client Invoices"
        description={`List of invoices for ${client.firstName} ${client.lastName} (${totalInvoices} total)`}
        showPagination={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortConfig={sortConfig}
      />
      {showInvoiceGenerator && currentInvoice && (
        <ViewInvoicePDFModal
          onClose={handleInvoiceGeneratorClose}
          source="invoice"
          data={{ invoiceId: currentInvoice._id }}
        />
      )}
    </div>
  );
};

export default ClientInvoices;
